import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Rss, CheckCircle, XCircle, Power
} from 'lucide-react';
import { Box, Grid, Paper, Typography, Button } from '@mui/material';

// Corrige ícones padrão do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Mock de sensores
const mockSensores = [
  { id: 1, nome: 'Sensor 01', status: 'online', tipo: 'temperatura', latitude: -23.346933, longitude: -47.887825 },
  { id: 2, nome: 'Sensor 02', status: 'offline', tipo: 'umidade', latitude: -23.348, longitude: -47.887 },
  { id: 3, nome: 'Sensor 03', status: 'online', tipo: 'luminosidade', latitude: -23.347, longitude: -47.888 },
  { id: 4, nome: 'Sensor 04', status: 'online', tipo: 'umidade', latitude: -23.346, longitude: -47.886 },
];

// Ícones personalizados por tipo
const tipoToIcon = {
  temperatura: new L.DivIcon({ html: '🌡️', className: '', iconSize: [36, 36] }),
  umidade: new L.DivIcon({ html: '💧', className: '', iconSize: [36, 36] }),
  luminosidade: new L.DivIcon({ html: '☀️', className: '', iconSize: [36, 36] }),
};

// Gráfico de tipos
const tipos = [...new Set(mockSensores.map(s => s.tipo))];
const contagemPorTipo = tipos.map(tipo => ({
  name: tipo,
  value: mockSensores.filter(s => s.tipo === tipo).length,
}));
const COLORS = ['#6366f1', '#34d399', '#f59e0b', '#06b6d4'];

const Sensors = () => {
  const [mapType, setMapType] = useState('satellite');

  const tileLayers = {
    default: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; OpenStreetMap contributors'
    },
    satellite: {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution: '&copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community'
    }
  };

  const handleToggleMapType = () => {
    setMapType(prev => (prev === 'default' ? 'satellite' : 'default'));
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: 4 }}>
      <Typography variant="h4" gutterBottom color="#1f2937">
        Sensores da Fazenda
      </Typography>

      {/* Cards resumo */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Rss className="mx-auto text-gray-600 mb-2" />
            <Typography variant="body2" color="textSecondary">Total de Sensores</Typography>
            <Typography variant="h5">{mockSensores.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircle className="mx-auto text-green-500 mb-2" />
            <Typography variant="body2" color="textSecondary">Online</Typography>
            <Typography variant="h5" color="green">
              {mockSensores.filter(s => s.status === 'online').length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <XCircle className="mx-auto text-red-500 mb-2" />
            <Typography variant="body2" color="textSecondary">Offline</Typography>
            <Typography variant="h5" color="error">
              {mockSensores.filter(s => s.status === 'offline').length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráfico de tipos */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom color="textPrimary">
          Distribuição por Tipo de Sensor
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={contagemPorTipo} dataKey="value" nameKey="name" outerRadius={90} label>
              {contagemPorTipo.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Paper>

      {/* Lista de sensores */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {mockSensores.map((sensor) => (
          <Grid item xs={12} md={6} lg={4} key={sensor.id}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderLeft: `6px solid ${sensor.status === 'online' ? '#34d399' : '#f87171'}`,
              }}
            >
              <Typography variant="h6">{sensor.nome}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tipo: <strong>{sensor.tipo}</strong> — Status:{' '}
                <span style={{ color: sensor.status === 'online' ? '#10b981' : '#ef4444' }}>
                  {sensor.status}
                </span>
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Power size={18} />}
                onClick={() => alert(`Ativando bomba do ${sensor.nome}`)}
              >
                Acionar bomba
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Mapa com botão de alternância */}
      <Typography variant="h6" gutterBottom color="textPrimary">
        Mapa de Localização
      </Typography>

      <Box sx={{ position: 'relative' }}>
        <Paper elevation={3} sx={{ overflow: 'hidden', height: 400 }}>
          <MapContainer
            center={[-23.346933, -47.887825]}
            zoom={16}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url={tileLayers[mapType].url}
              attribution={tileLayers[mapType].attribution}
            />
            {mockSensores.map(sensor => (
              <Marker
                key={sensor.id}
                position={[sensor.latitude, sensor.longitude]}
                icon={tipoToIcon[sensor.tipo] || L.icon()}
              >
                <Popup>
                  <strong>{sensor.nome}</strong><br />
                  {sensor.tipo} — {sensor.status}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Paper>
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 1000,
            backgroundColor: 'white',
            borderRadius: '20px',
            boxShadow: 2,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 500,
            color: '#1e3a8a',
            transition: 'background 0.2s ease-in-out',
            '&:hover': { backgroundColor: '#e0e7ff' }
          }}
          onClick={handleToggleMapType}
        >
          {mapType === 'default' ? '🌍 Satélite' : '🗺️ Mapa Padrão'}
        </Box>
      </Box>
    </Box>
  );
};

export default Sensors;